import Swiper from "swiper";
import "swiper/css";
import { Autoplay, Navigation } from "swiper/modules";

/**
 * Клас для ініціалізації Swiper
 *
 * @package AGDS
 * @category Modules
 * @module Swiper
 * @see AGDSApp
 * @author Andy Groove
 * @since 1.0.0
 */
export default class SwiperSlider {
    /**
     * Конструктор
     *
     * @param {Object} options
     */
    constructor(options = {}) {
        this.windowWidth = options.windowWidth;
        this.swiperEls = options.swiperEls;

        this.defaultOptions = {
            speed: 800,
            slidesPerView: 1,
            spaceBetween: 16,
        };
    }

    /**
     * Ініціалізація
     */
    init() {
        if (typeof Swiper === "undefined") {
            console.error("Swiper is not defined");
            return;
        }

        // Слайдери
        this.partnersSliderInit();
        this.postsRelatedSliderInit();
        this.gallerySliderInit();
        this.teamSliderInit();
        this.casesSliderInit();
    }

    /**
     * Створює слайдер і зливає (merges) налаштування
     * @param {HTMLElement|string} elementOrSelector - DOM-елемент або CSS селектор
     * @param {Object} customOptions - Унікальні параметри для цього слайдера
     * @returns {Array} Масив інстансів Swiper
     */
    createSlider(elementOrSelector, customOptions = {}) {
        const elements =
            typeof elementOrSelector === "string" ? document.querySelectorAll(elementOrSelector) : [elementOrSelector];

        if (!elements || elements.length === 0) return;

        // Зливаємо дефолтні опції з кастомними.
        const finalOptions = {
            ...this.defaultOptions,
            ...customOptions,
            breakpoints: {
                ...this.defaultOptions.breakpoints,
                ...(customOptions.breakpoints || {}),
            },
        };

        const instances = [];

        elements.forEach((el) => {
            if (el.classList.contains("swiper-initialized")) {
                return;
            }

            const swiper = new Swiper(el, finalOptions);

            // Класи
            swiper.on("init", (s) => {
                if (s.params.centeredSlides) {
                    s.el.classList.add("swiper-centered-slides");
                }
                if (s.params.loop) {
                    s.el.classList.add("swiper-is-loop");
                }
            });

            // Якщо Swiper вже ініціалізувався (іноді буває миттєво),
            // викликаємо init вручну один раз для класів
            if (swiper.initialized) {
                swiper.emit("init");
            }

            instances.push(swiper);
        });

        return instances;
    }

    /**
     * Слайдер Partners
     */
    partnersSliderInit() {
        const sliders = document.querySelectorAll(".partners-slider");
        const stepDelay = 1000;
        const cycleDelay = stepDelay * sliders.length;

        sliders.forEach((slider, index) => {
            const instances = this.createSlider(slider, {
                modules: [Autoplay],
                autoplay: {
                    enabled: false,
                    delay: cycleDelay,
                    disableOnInteraction: false,
                },
                loop: true,
                breakpoints: {
                    1200: { slidesPerView: 4.5, spaceBetween: 0 },
                    768: { slidesPerView: 3.5, spaceBetween: 0 },
                    320: { slidesPerView: 1.5, spaceBetween: 0 },
                },
            });
            const swiper = instances?.[0];

            window.setTimeout(() => {
                if (!swiper || swiper.destroyed) return;

                swiper.slideNext();
                swiper.autoplay.start();
            }, stepDelay * (index + 1));
        });
    }

    /**
     * Слайдер Team
     */
    teamSliderInit() {
        if (this.windowWidth > 992) return;

        this.createSlider(".team-slider", {
            breakpoints: {
                768: { slidesPerView: 2.5, spaceBetween: 16 },
                320: { slidesPerView: 1.2, spaceBetween: 16 },
            },
        });
    }

    /**
     * Слайдер Cases
     */
    casesSliderInit() {
        this.createSlider(".cases-slider", {
            modules: [Navigation],
            navigation: {
                nextEl: ".slider-button--next",
                prevEl: ".slider-button--prev",
            },
            breakpoints: {
                1200: { slidesPerView: 3.5, spaceBetween: 4 },
                992: { slidesPerView: 3, spaceBetween: 4 },
                768: { slidesPerView: 2.5, spaceBetween: 4 },
                320: { slidesPerView: 1.2, spaceBetween: 4 },
            },
        });
    }

    /**
     * Слайдер Posts Related
     */
    postsRelatedSliderInit() {
        this.createSlider(".posts-related-slider", {
            modules: [Navigation],
            navigation: {
                nextEl: ".slider-button--next",
                prevEl: ".slider-button--prev",
            },
            breakpoints: {
                992: { slidesPerView: 3, spaceBetween: 20 },
                768: { slidesPerView: 2, spaceBetween: 16 },
                320: { slidesPerView: 1.1, spaceBetween: 16 },
            },
        });
    }

    /**
     * Слайдер Gallery
     */
    gallerySliderInit() {
        this.createSlider(".gallery-slider", {
            modules: [Navigation],
            loop: true,
            navigation: {
                nextEl: ".slider-button--next",
                prevEl: ".slider-button--prev",
            },
            breakpoints: {
                320: { slidesPerView: 1, spaceBetween: 10 },
            },
        });
    }
}
